import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import APRmemo from "./APRComment.js/APRmemo";
import APRComment from "./APRComment.js/AprComments";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

// Color palette for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <p style={{ fontWeight: 'bold', margin: '0 0 5px 0' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color, margin: '2px 0' }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Annex5b Component
const Annex5b = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [showCharts, setShowCharts] = useState(true);

  useEffect(() => {
    fetchStaffData(district);
  }, [district, year, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Annex_5b",
      tableData
    }),
  }));

  function fetchStaffData(regionId) {
    axios
      .get(`/tracker/trackedEntities`, {
        params: {
          orgUnit: regionId,
          ouMode: 'CHILDREN',
          program: 'AJDfCnHCQ2j',
          pageSize: 5000
        }
      })
      .then(result => {
        const staffList = result.data.instances.filter(
          instance => instance.orgUnit !== regionId
        );

        const districtsInRegion = [];
        const storedDistricts = localStorage.getItem("districts");
        if (storedDistricts) {
          const parsedDistricts = JSON.parse(storedDistricts);
          districtsInRegion.push(...parsedDistricts);
        }

        const temp = [];
        staffList.forEach(val => {
          const dataSet = {
            orgUnitId: val.orgUnit,
            mmda: districtsInRegion.find(d => d.value.id === val.orgUnit)?.label || "",
            nameOfMmdce: "",
            gender: getAttributeValue("Sex", val),
          };
          temp.push(dataSet);
        });

        const districtMap = new Map();
        temp.forEach(item => {
          if (!districtMap.has(item.orgUnitId)) {
            districtMap.set(item.orgUnitId, {
              orgUnitId: item.orgUnitId,
              mmda: item.mmda,
              nameOfMmdce: item.nameOfMmdce,
              totalMale: 0,
              totalFemale: 0
            });
          }
          const district = districtMap.get(item.orgUnitId);
          if (item.gender === "Male") {
            district.totalMale++;
          } else if (item.gender === "Female") {
            district.totalFemale++;
          }
        });

        const aggregatedData = Array.from(districtMap.values());
        setTableData(aggregatedData);
      })
      .catch(err => console.log(err))
  }

  // Prepare data for charts (limit to top districts for better visibility)
  const getTopDistrictsData = (limit = 20) => {
    return [...tableData]
      .sort((a, b) => (b.totalMale + b.totalFemale) - (a.totalMale + a.totalFemale))
      .slice(0, limit)
      .map(row => ({
        name: row.mmda.length > 20 ? row.mmda.substring(0, 20) + '...' : row.mmda,
        fullName: row.mmda,
        Male: row.totalMale,
        Female: row.totalFemale,
        Total: row.totalMale + row.totalFemale
      }));
  };

  // Prepare data for gender distribution pie chart
  const getGenderDistribution = () => {
    const totalMale = tableData.reduce((sum, row) => sum + row.totalMale, 0);
    const totalFemale = tableData.reduce((sum, row) => sum + row.totalFemale, 0);
    return [
      { name: 'Male', value: totalMale },
      { name: 'Female', value: totalFemale }
    ];
  };

  const topDistrictsData = getTopDistrictsData();
  const genderDistribution = getGenderDistribution();
  const totalAssembly = tableData.reduce((sum, row) => sum + row.totalMale + row.totalFemale, 0);

  return (
    <div className="col-12">
      <h3>Update On Human Resource Issues and Logistics</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table19-${year}`}
            hideTableDis={hideTableDis}
          />
          
          {/* Table Section */}
          <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
            <table className="table table-bordered" style={{ width: '100%', minWidth: '600px' }}>
              <thead style={{ backgroundColor: '#d4edda', fontWeight: 'bold', border: '1px solid #000' }}>
                <tr>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>S/N</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>MMDA</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>NAME OF MMDCE</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>ASSEMBLY MEMBERS MALE</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>ASSEMBLY MEMBERS FEMALE</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>ASSEMBLY MEMBERS TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td style={{ border: '1px solid #000' }}>{index + 1}</td>
                    <td style={{ border: '1px solid #000' }}>{row.mmda}</td>
                    <td style={{ border: '1px solid #000' }}>{row.nameOfMmdce}</td>
                    <td style={{ border: '1px solid #000' }}>{row.totalMale}</td>
                    <td style={{ border: '1px solid #000' }}>{row.totalFemale}</td>
                    <td style={{ border: '1px solid #000' }}>{row.totalMale + row.totalFemale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="mt-2">
            <small>Source: {year} Training Reports</small>
          </p>
          
          {/* Chart Toggle Button */}
          <div className="mb-3">
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => setShowCharts(!showCharts)}
            >
              {showCharts ? 'Hide Charts' : 'Show Charts'}
            </button>
          </div>

          {/* Charts Section - After Table */}
          {showCharts && tableData.length > 0 && (
            <>
              {/* Graph 1: Assembly Members Distribution by District */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">
                        Assembly Members Distribution by District 
                        {tableData.length > 20 && ` (Top 20 of ${tableData.length} districts shown)`}
                      </h5>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={topDistrictsData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45} 
                            textAnchor="end" 
                            height={100} 
                            interval={0}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="Male" fill="#0088FE" name="Male" />
                          <Bar dataKey="Female" fill="#00C49F" name="Female" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Graph 2: Gender Distribution Summary */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h5 className="mb-0">Gender Distribution Summary</h5>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                          <Pie
                            data={genderDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}% (${name === 'Male' ? genderDistribution[0].value : genderDistribution[1].value} members)`}
                            outerRadius={150}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {genderDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                      
                      {/* Summary statistics below pie chart */}
                      <div className="row mt-4">
                        <div className="col-md-6">
                          <div className="alert alert-info">
                            <strong>Total Assembly Members:</strong> {totalAssembly}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="alert alert-success">
                            <strong>Female Representation:</strong> {totalAssembly > 0 ? ((genderDistribution[1].value / totalAssembly) * 100).toFixed(1) : 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table19-${year}`}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Annex5b;